import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { DeviceMobileIcon as DeviceMobile, AppleLogoIcon as AppleLogo, AndroidLogoIcon as AndroidLogo, DesktopIcon as Desktop, CheckCircleIcon as CheckCircle, CopyIcon as Copy, ShareIcon as Share, PlusIcon as Plus, ArrowDownIcon as ArrowDown, InfoIcon as Info, WarningIcon as Warning, ArrowSquareOutIcon as ArrowSquareOut, DownloadSimpleIcon as DownloadSimple, EnvelopeSimpleIcon as EnvelopeSimple, ShareNetworkIcon as ShareNetwork, PrinterIcon as Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { composeInstallPoster, downloadBlob, canShareFiles } from "@/lib/installPoster";

/**
 * Dedicated install page — bookmarkable, shareable, browser-aware.
 *
 * The pain point this page solves: 60% of mobile traffic on iOS uses
 * Chrome, Firefox, Edge, or in-app browsers (Instagram, Twitter,
 * Gmail). Those browsers literally CANNOT install a PWA on iOS — only
 * Safari can. Sending them generic "Add to Home Screen" instructions
 * makes them hunt for a Share button that isn&rsquo;t there.
 *
 * Detection routes a visitor into ONE of five lanes:
 *   1. Already installed         → green "you're set" + push CTA
 *   2. iOS Safari                → animated Share-button instructions
 *   3. iOS non-Safari            → copy-address + open-Safari path
 *   4. Android Chrome (+desktop) → programmatic beforeinstallprompt
 *   5. Desktop fallback          → QR code for phone hand-off
 *
 * No Twilio yet — SMS send-to-phone is queued as a follow-up that
 * requires a fresh integration. QR + copy-link covers the same use
 * case zero-cost, today.
 */

// Public URL used in install instructions + the QR code. Order:
// 1) explicit env var, 2) current origin (preview matches prod
// behavior closely enough). Falls back to the production hostname
// only as a final safety net.
function getInstallUrl() {
  if (typeof window === "undefined") return "https://armstrongk9registry.com/install";
  const envUrl = process.env.REACT_APP_PUBLIC_URL;
  const base = envUrl || window.location.origin;
  return `${base.replace(/\/$/, "")}/install`;
}

function detectPlatform() {
  if (typeof window === "undefined") return "ssr";
  const ua = (navigator.userAgent || "").toLowerCase();
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (standalone) return "installed";
  const isIos = /iphone|ipad|ipod/.test(ua);
  if (isIos) {
    // Safari iOS user-agent contains "safari" + "version/" but NOT
    // "crios" (Chrome), "fxios" (Firefox), "edgios" (Edge), or
    // "criios" (other Chromium-based iOS browsers).
    const isCrios = /crios|fxios|edgios|firefox|opera|opios|mercury/i.test(navigator.userAgent || "");
    const isInstagram = /instagram/i.test(navigator.userAgent || "");
    const isFacebook = /fban|fbav/i.test(navigator.userAgent || "");
    if (isCrios || isInstagram || isFacebook) return "ios-other";
    return "ios-safari";
  }
  const isAndroid = /android/.test(ua);
  if (isAndroid) return "android";
  return "desktop";
}

export default function Install() {
  const [platform, setPlatform] = useState("ssr");
  const [installEvent, setInstallEvent] = useState(null);
  const [installResult, setInstallResult] = useState(null); // "accepted" | "dismissed"
  const installUrl = useRef(getInstallUrl());

  useEffect(() => {
    setPlatform(detectPlatform());
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const onInstalled = () => setInstallResult("accepted");
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const copyAddress = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Address copied. Paste it into Safari.");
    } catch {
      // Older iOS in-app browsers can refuse clipboard API. Surface a
      // selectable text block so the user can long-press to copy.
      toast.error("Couldn&rsquo;t copy automatically — long-press the address to copy it manually.");
    }
  };

  const programmaticInstall = async () => {
    if (!installEvent) return;
    try {
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      setInstallResult(choice.outcome);
      if (choice.outcome === "accepted") {
        toast.success("Armstrong K9 installed. Open it from your home screen.");
      }
    } catch {
      /* user dismissed */
    }
    setInstallEvent(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/40">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="text-center mb-8" data-testid="install-page-header">
          <img
            src="/logo-256.png"
            alt="Armstrong K9 Registry"
            className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-md"
          />
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
            Install the app
          </p>
          <h1 className="font-spectral font-light text-3xl sm:text-4xl tracking-tight text-ink leading-tight">
            One tap to your home screen.
          </h1>
          <p className="mt-3 text-[14px] text-[#6B6B6B] leading-relaxed max-w-md mx-auto">
            Push notifications for bids, inquiries, transfers and
            payouts. Faster load. Works offline for everything you&rsquo;ve
            already loaded.
          </p>
        </div>

        {platform === "installed" && <AlreadyInstalled />}
        {platform === "ios-safari" && <IosSafariInstructions />}
        {platform === "ios-other" && (
          <IosOtherBrowserInstructions
            installUrl={installUrl.current}
            onCopy={() => copyAddress(installUrl.current)}
          />
        )}
        {platform === "android" && (
          <AndroidInstructions
            hasInstallEvent={!!installEvent}
            onInstall={programmaticInstall}
            installed={installResult === "accepted"}
          />
        )}
        {platform === "desktop" && (
          <DesktopInstructions
            installUrl={installUrl.current}
            hasInstallEvent={!!installEvent}
            onInstall={programmaticInstall}
            installed={installResult === "accepted"}
          />
        )}
        {platform === "ssr" && (
          <div className="rounded-2xl border border-[#EAEAEA] bg-white p-6 text-center text-[#6B6B6B]">
            Detecting your browser…
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink hover:text-crimson underline-offset-4 hover:underline"
            data-testid="install-page-back"
          >
            Skip for now &mdash; take me to the app
          </Link>
        </div>

        <p className="mt-12 text-[11px] text-[#9A9A9A] text-center leading-relaxed">
          Installing creates a home-screen shortcut. It doesn&rsquo;t
          download anything from the App Store and doesn&rsquo;t cost
          anything.
        </p>
      </div>
    </div>
  );
}

// ----- Sub-renders ---------------------------------------------------

const APP_STORE_URL =
  "https://apps.apple.com/us/app/armstrong-k9-registry/id6790544132";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.armstrongk9registry";

function AlreadyInstalled() {
  return (
    <div
      className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6 text-center"
      data-testid="install-already-installed"
    >
      <CheckCircle size={36} weight="fill" className="text-emerald-600 mx-auto mb-3" />
      <h2 className="font-spectral text-2xl text-ink leading-tight">You&rsquo;re set.</h2>
      <p className="text-[13.5px] text-[#3F3F3F] mt-2">
        You&rsquo;re using the installed app right now. Find it on your
        home screen any time.
      </p>
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 mt-5 rounded-full bg-ink hover:bg-crimson text-white px-5 py-3 text-[13px] font-bold"
      >
        Open my dashboard
      </Link>
    </div>
  );
}

function Step({ n, children, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink text-white font-bold text-[12px] flex-shrink-0 font-tabular">
        {n}
      </span>
      <div className="flex-1 text-[14px] text-[#3F3F3F] leading-relaxed pt-0.5">{children}</div>
    </div>
  );
}

function IosSafariInstructions() {
  return (
    <div
      className="rounded-2xl border border-gold/40 bg-white p-6 sm:p-8"
      data-testid="install-ios-safari"
    >
      <div className="flex items-center gap-2 mb-1">
        <AppleLogo size={20} weight="fill" className="text-ink" />
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold">
          You&rsquo;re in Safari on iPhone or iPad
        </p>
      </div>
      <h2 className="font-spectral text-2xl text-ink mb-4 leading-tight">
        The app is on the App Store.
      </h2>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-crimson text-white h-12 px-6 text-[14px] font-bold"
        data-testid="install-ios-appstore-cta"
      >
        <AppleLogo size={16} weight="fill" />
        Download on the App Store
      </a>
      <p className="text-[12.5px] text-[#6B6B6B] mt-5 mb-4">
        Prefer the lightweight web app instead? Add it straight from
        Safari — three taps:
      </p>

      <div className="space-y-4">
        <Step n={1} testid="ios-safari-step-1">
          Tap the <strong>Share</strong> button at the
          <strong> bottom</strong> of your screen.
          <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-cream/50 border border-[#E2DAC2] px-3 py-2 text-[12px] text-[#6B6B6B]">
            <Share size={16} weight="duotone" className="text-ink" />
            It&rsquo;s the square with an upward arrow.
          </div>
        </Step>
        <Step n={2} testid="ios-safari-step-2">
          Scroll down the share sheet and tap <strong>Add to Home Screen</strong>.
          <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-cream/50 border border-[#E2DAC2] px-3 py-2 text-[12px] text-[#6B6B6B]">
            <Plus size={16} weight="bold" className="text-ink" />
            Look for the &ldquo;+&rdquo; icon next to it.
          </div>
        </Step>
        <Step n={3} testid="ios-safari-step-3">
          Tap <strong>Add</strong> in the top-right of the dialog.
          That&rsquo;s it — Armstrong K9 is now on your home screen.
        </Step>
      </div>

      <div className="mt-6 flex items-center justify-center text-[#9A9A9A]">
        <ArrowDown size={28} weight="bold" className="animate-bounce" />
      </div>
      <p className="text-center text-[12px] text-[#6B6B6B] -mt-1">
        Look for the Share button at the bottom of Safari.
      </p>

      <div className="mt-6 rounded-lg bg-[#FBF7EC] border border-gold/30 p-3 text-[12px] text-[#3F3F3F] leading-relaxed">
        <Info size={14} weight="duotone" className="text-gold-dark inline mr-1.5 -mt-0.5" />
        After install, open Armstrong K9 from your home screen and
        come back here once to turn on push notifications.
      </div>
    </div>
  );
}

function IosOtherBrowserInstructions({ installUrl, onCopy }) {
  return (
    <div
      className="rounded-2xl border border-crimson/30 bg-white p-6 sm:p-8"
      data-testid="install-ios-other"
    >
      <div className="flex items-center gap-2 mb-1">
        <Warning size={18} weight="fill" className="text-crimson" />
        <p className="text-[10px] uppercase tracking-[0.22em] text-crimson font-bold">
          You&rsquo;re not in Safari
        </p>
      </div>
      <h2 className="font-spectral text-2xl text-ink mb-4 leading-tight">
        Grab it from the App Store.
      </h2>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-crimson text-white h-12 px-6 text-[14px] font-bold"
        data-testid="install-ios-other-appstore-cta"
      >
        <AppleLogo size={16} weight="fill" />
        Download on the App Store
      </a>
      <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed mt-5 mb-5">
        Prefer the web app? Chrome, Firefox, Edge, and in-app browsers
        (Instagram, Twitter, Gmail) can&rsquo;t install web apps on iOS —
        that&rsquo;s Apple&rsquo;s rule, not ours. Here&rsquo;s the workaround:
      </p>

      <div className="space-y-4">
        <Step n={1} testid="ios-other-step-1">
          Tap the button below to copy the address.
          <div className="mt-3">
            <Button
              onClick={onCopy}
              className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-5 text-[13px] font-bold"
              data-testid="install-ios-other-copy"
            >
              <Copy size={14} weight="bold" className="mr-1.5" />
              Copy address
            </Button>
          </div>
          <div className="mt-3 rounded-lg border border-[#E2DAC2] bg-cream/30 px-3 py-2 text-[12px] font-mono break-all select-all text-[#3F3F3F]">
            {installUrl}
          </div>
        </Step>
        <Step n={2} testid="ios-other-step-2">
          <strong>Open Safari</strong> on this phone (the blue compass
          icon). It&rsquo;s pre-installed on every iPhone.
        </Step>
        <Step n={3} testid="ios-other-step-3">
          Tap the address bar at the top, <strong>paste the address</strong>,
          and tap Go. You&rsquo;ll land on this page again — and Safari
          will give you the &ldquo;Add to Home Screen&rdquo; option this
          time.
        </Step>
      </div>

    </div>
  );
}

function AndroidInstructions({ hasInstallEvent, onInstall, installed }) {
  return (
    <div
      className="rounded-2xl border border-gold/40 bg-white p-6 sm:p-8"
      data-testid="install-android"
    >
      <div className="flex items-center gap-2 mb-1">
        <AndroidLogo size={20} weight="fill" className="text-emerald-700" />
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold">
          You&rsquo;re on Android
        </p>
      </div>
      <h2 className="font-spectral text-2xl text-ink mb-4 leading-tight">
        The app is on Google Play.
      </h2>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-crimson text-white h-12 px-6 text-[14px] font-bold mb-5"
        data-testid="install-android-playstore-cta"
      >
        <AndroidLogo size={16} weight="fill" />
        Get it on Google Play
      </a>
      <p className="text-[12.5px] text-[#6B6B6B] mb-4">
        Prefer the lightweight web app instead?
      </p>

      {installed ? (
        <div className="rounded-lg bg-emerald-50 border border-emerald-300 px-4 py-3 text-[13px] text-emerald-800">
          <CheckCircle size={15} weight="fill" className="inline mr-1.5 -mt-0.5" />
          Installed. Look for Armstrong K9 in your app drawer.
        </div>
      ) : hasInstallEvent ? (
        <>
          <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed mb-4">
            Your browser is ready to install Armstrong K9 Registry as
            an app. Tap below and confirm.
          </p>
          <Button
            onClick={onInstall}
            className="rounded-full bg-ink hover:bg-crimson text-white h-12 px-6 text-[14px] font-bold w-full sm:w-auto"
            data-testid="install-android-cta"
          >
            <DeviceMobile size={16} weight="duotone" className="mr-2" />
            Install Armstrong K9
          </Button>
        </>
      ) : (
        <div className="space-y-4">
          <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed">
            Your browser didn&rsquo;t fire the auto-install prompt — no
            problem, you can install manually:
          </p>
          <Step n={1}>
            Tap the <strong>three-dot menu</strong> in the top-right corner of
            your browser.
          </Step>
          <Step n={2}>
            Tap <strong>Install app</strong> or <strong>Add to home screen</strong>.
          </Step>
          <Step n={3}>
            Confirm in the dialog that appears.
          </Step>
        </div>
      )}
    </div>
  );
}

function DesktopInstructions({ installUrl, hasInstallEvent, onInstall, installed }) {
  // Render the off-screen QR canvas at a high resolution so the
  // poster keeps sharp modules even at print size. The composer
  // scales it down to the poster's 760px slot.
  const QR_PX_OFFSCREEN = 760;
  // Hidden QRCodeCanvas — same value as the visible SVG. We need a
  // canvas reference (not SVG) to compose the downloadable poster.
  const qrCanvasRef = useRef(null);
  const [busyShare, setBusyShare] = useState(null); // 'download' | 'share' | 'print'

  const subject = "Get Armstrong K9 Registry on your phone";
  const bodyLines = [
    "Here's the link to install Armstrong K9 Registry on your phone:",
    "",
    installUrl,
    "",
    "Push notifications for bids, inquiries, transfers and payouts. No App Store needed — works on iPhone & Android.",
  ];
  const mailto =
    `mailto:?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(bodyLines.join("\n"))}`;

  const buildPoster = async () => {
    const canvas = qrCanvasRef.current?.querySelector("canvas");
    if (!canvas) throw new Error("QR canvas not ready yet");
    return composeInstallPoster(canvas, installUrl);
  };

  const onDownload = async () => {
    setBusyShare("download");
    try {
      const blob = await buildPoster();
      downloadBlob(blob, "armstrong-k9-install.png");
      toast.success("Poster downloaded. Print it or share it anywhere.");
    } catch (e) {
      toast.error("Couldn't build the poster. Try again in a moment.");
    } finally {
      setBusyShare(null);
    }
  };

  const onNativeShare = async () => {
    setBusyShare("share");
    try {
      const blob = await buildPoster();
      const file = new File([blob], "armstrong-k9-install.png", { type: "image/png" });
      if (canShareFiles(file)) {
        await navigator.share({
          files: [file],
          title: "Install Armstrong K9 Registry",
          text: `Get Armstrong K9 Registry on your phone: ${installUrl}`,
        });
      } else if (typeof navigator.share === "function") {
        // Browser supports share() but not file sharing. Share text only.
        await navigator.share({
          title: "Install Armstrong K9 Registry",
          text: `Get Armstrong K9 Registry on your phone: ${installUrl}`,
          url: installUrl,
        });
      } else {
        // No Web Share API at all (most desktops). Fall through to copy.
        await navigator.clipboard.writeText(installUrl);
        toast.success("Link copied — paste it anywhere.");
      }
    } catch (e) {
      // AbortError = user cancelled the share sheet. Don't toast that.
      if (e?.name !== "AbortError") {
        toast.error("Couldn't open the share sheet.");
      }
    } finally {
      setBusyShare(null);
    }
  };

  const onPrint = async () => {
    setBusyShare("print");
    try {
      const blob = await buildPoster();
      const url = URL.createObjectURL(blob);
      // Open the PNG in a new tab and trigger the browser print
      // dialog. Most browsers honor window.print() inside the popup
      // before it finishes loading the image, so we wait briefly.
      const w = window.open(url, "_blank");
      if (w) {
        setTimeout(() => {
          try { w.print(); } catch { /* user can hit print themselves */ }
        }, 800);
      } else {
        // Popup blocked. Fall back to direct download.
        downloadBlob(blob, "armstrong-k9-install.png");
        toast.info("Popup blocked — poster downloaded. Open it and print from there.");
      }
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error("Couldn't open the print view.");
    } finally {
      setBusyShare(null);
    }
  };

  return (
    <div
      className="rounded-2xl border border-gold/40 bg-white p-6 sm:p-8"
      data-testid="install-desktop"
    >
      <div className="flex items-center gap-2 mb-1">
        <Desktop size={20} weight="duotone" className="text-ink" />
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold">
          You&rsquo;re on a desktop
        </p>
      </div>
      <h2 className="font-spectral text-2xl text-ink mb-3 leading-tight">
        Want it on your phone? Scan this.
      </h2>
      <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed mb-6">
        Open your phone&rsquo;s camera, point it at the code below, and
        tap the link that pops up. Or share the poster &mdash;
        post it on social, email it, print it for your kennel wall.
      </p>

      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div
          className="rounded-xl border border-[#EAEAEA] bg-white p-3 shadow-sm flex-shrink-0"
          data-testid="install-desktop-qr"
        >
          <QRCodeSVG
            value={installUrl}
            size={180}
            includeMargin={false}
            level="M"
            bgColor="#FFFFFF"
            fgColor="#0E0E0E"
          />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <p className="text-[12px] text-[#6B6B6B] mb-2">Or copy the link manually:</p>
          <div className="rounded-lg border border-[#E2DAC2] bg-cream/30 px-3 py-2 text-[12px] font-mono break-all select-all text-[#3F3F3F]">
            {installUrl}
          </div>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(installUrl);
                toast.success("Link copied. Send it to your phone any way you like.");
              } catch {
                toast.error("Couldn't copy. Long-press the link to copy it manually.");
              }
            }}
            className="mt-3 rounded-full h-10 px-4 text-[13px]"
            data-testid="install-desktop-copy"
          >
            <Copy size={13} weight="bold" className="mr-1.5" />
            Copy install link
          </Button>
        </div>
      </div>

      {/* Share the poster ------------------------------------------- */}
      <div
        className="mt-6 pt-6 border-t border-[#EAEAEA]"
        data-testid="install-desktop-share-section"
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold mb-2">
          Share the QR poster
        </p>
        <p className="text-[12.5px] text-[#6B6B6B] leading-relaxed mb-3">
          Download a branded PNG &mdash; print it for your kennel,
          email it to clients, or post it to social.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onDownload}
            disabled={busyShare !== null}
            className="rounded-full bg-ink hover:bg-crimson text-white h-10 px-4 text-[13px] font-bold"
            data-testid="install-share-download"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            {busyShare === "download" ? "Building…" : "Download poster"}
          </Button>
          <Button
            variant="outline"
            onClick={onNativeShare}
            disabled={busyShare !== null}
            className="rounded-full h-10 px-4 text-[13px]"
            data-testid="install-share-native"
          >
            <ShareNetwork size={13} weight="duotone" className="mr-1.5" />
            {busyShare === "share" ? "Opening…" : "Share…"}
          </Button>
          <Button
            asChild
            variant="outline"
            className="rounded-full h-10 px-4 text-[13px]"
            data-testid="install-share-email"
          >
            <a href={mailto}>
              <EnvelopeSimple size={13} weight="duotone" className="mr-1.5" />
              Email link
            </a>
          </Button>
          <Button
            variant="outline"
            onClick={onPrint}
            disabled={busyShare !== null}
            className="rounded-full h-10 px-4 text-[13px]"
            data-testid="install-share-print"
          >
            <Printer size={13} weight="duotone" className="mr-1.5" />
            {busyShare === "print" ? "Opening…" : "Print"}
          </Button>
        </div>
        <p className="mt-3 text-[11px] text-[#9A9A9A] leading-relaxed">
          The poster is a single-page 1200&times;1600 PNG with the QR
          code, the install URL, and the Armstrong K9 wordmark.
        </p>
      </div>

      {/* Hidden QRCodeCanvas — kept off-screen so we can read its
          pixels into the poster canvas. The visible QR above is an
          SVG (sharper at any zoom); this canvas is purely a source
          for compositing. */}
      <div ref={qrCanvasRef} style={{ position: "absolute", left: "-9999px", top: 0 }} aria-hidden="true">
        <QRCodeCanvas
          value={installUrl}
          size={QR_PX_OFFSCREEN}
          includeMargin={false}
          level="M"
          bgColor="#FFFFFF"
          fgColor="#0E0E0E"
        />
      </div>

      {installed ? (
        <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-300 px-4 py-3 text-[13px] text-emerald-800">
          <CheckCircle size={15} weight="fill" className="inline mr-1.5 -mt-0.5" />
          Installed on this computer. Find Armstrong K9 in your apps.
        </div>
      ) : hasInstallEvent ? (
        <div className="mt-6">
          <p className="text-[12.5px] text-[#6B6B6B] mb-2">
            You can also install Armstrong K9 on this computer:
          </p>
          <Button
            onClick={onInstall}
            className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-5 text-[13px] font-bold"
            data-testid="install-desktop-cta"
          >
            <Desktop size={15} weight="duotone" className="mr-1.5" />
            Install on this computer
          </Button>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-ink/20 hover:border-ink text-ink h-10 px-4 text-[12.5px] font-bold"
          data-testid="install-desktop-appstore-link"
        >
          <AppleLogo size={14} weight="fill" />
          iPhone: App Store <ArrowSquareOut size={11} weight="bold" />
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-ink/20 hover:border-ink text-ink h-10 px-4 text-[12.5px] font-bold"
          data-testid="install-desktop-playstore-link"
        >
          <AndroidLogo size={14} weight="fill" />
          Android: Google Play <ArrowSquareOut size={11} weight="bold" />
        </a>
      </div>
    </div>
  );
}
